// frontend/src/pages/DataExplorerPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { apiGet, apiPost, listify, apiGetAll } from "../api";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";


const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DataExplorerPage({ user }) {
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [yearEnd, setYearEnd] = useState("");
  const [rowsTime, setRowsTime] = useState([]);
  const [rowsInv, setRowsInv] = useState([]);
  const [rowsWO, setRowsWO] = useState([]);
  const [err, setErr] = useState("");
  const [clientQuery, setClientQuery] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();

  const [woForm, setWoForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: "",
    reason: "",
    year_end: ""
  });

  // Read URL parameters
  useEffect(() => {
    const clientParam = searchParams.get("client");
    const yearParam = searchParams.get("year");
    const startParam = searchParams.get("start");
    const endParam = searchParams.get("end");

    if (clientParam) setClientFilter(clientParam);
    if (yearParam) setYearEnd(yearParam);
    if (startParam) setStartDate(startParam);
    if (endParam) setEndDate(endParam);

    if (clients.length > 0 && clientParam) {
      const client = clients.find((c) => String(c.id) === String(clientParam));
      if (client) setClientQuery(client.name);
    }
  }, [searchParams, clients]);

  const who = (r) =>
    r?.user_name ||
    r?.user_full_name ||
    r?.employee_name ||
    r?.created_by_name ||
    r?.created_by ||
    r?.user ||
    "—";

  // load clients
  useEffect(() => {
    (async () => {
      try {
        const c = await apiGet("/api/clients/");
        setClients(listify(c));
      } catch (e) {
        setErr(e.message);
      }
    })();
  }, []);

  // load employees
  useEffect(() => {
    (async () => {
      try {
        const e = await apiGet("/api/employees/");
        setEmployees(listify(e));
      } catch (e) {
        setErr(e.message);
      }
    })();
  }, []);

  // load data
  useEffect(() => {
    (async () => {
      if (!clientFilter) {
        setRowsTime([]);
        setRowsInv([]);
        setRowsWO([]);
        return;
      }
      
      try {
        // Build query strings for each endpoint
        const timeQs = new URLSearchParams();
        const invQs = new URLSearchParams();
        const woQs = new URLSearchParams();
        
        // Add common filters
        if (clientFilter) {
          timeQs.append("client", clientFilter);
          invQs.append("client", clientFilter);
          woQs.append("client", clientFilter);
        }
        if (employeeFilter) timeQs.append("user", employeeFilter);
        if (startDate) {
          timeQs.append("start_date", startDate);
          invQs.append("start_date", startDate);
          woQs.append("start_date", startDate);
        }
        if (endDate) {
          timeQs.append("end_date", endDate);
          invQs.append("end_date", endDate);
          woQs.append("end_date", endDate);
        }

        const [t, inv, wos] = await Promise.all([
          apiGetAll(`/api/time-entries/?${timeQs.toString()}`),
          apiGet(`/api/invoices/?${invQs.toString()}`),
          apiGet(`/api/write-offs/?${woQs.toString()}`),
        ]);
        
        let timeData = listify(t);
        let invData = listify(inv);
        let woData = listify(wos);
        
        // Client-side filtering by year_end if provided
        if (yearEnd) {
          timeData = timeData.filter(item => String(item.year_end) === String(yearEnd));
          invData = invData.filter(item => String(item.year_end) === String(yearEnd));
          woData = woData.filter(item => String(item.year_end) === String(yearEnd));
        }
        
        setRowsTime(timeData);
        setRowsInv(invData);
        setRowsWO(woData);
      } catch (e) {
        setErr(e.message);
      }
    })();
  }, [clientFilter, employeeFilter, startDate, endDate, yearEnd]);

  // Calculate summary metrics
  const summary = useMemo(() => {
    const totalHours = rowsTime.reduce((sum, t) => sum + Number(t.hours || 0), 0);
    const totalTime = rowsTime.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    const totalInvoiced = rowsInv.reduce((sum, i) => sum + Number(i.total || 0), 0);
    const totalWriteoffs = rowsWO.reduce((sum, w) => sum + Number(w.amount || 0), 0);
    const totalAR = rowsInv
      .filter(i => i.status && i.status.toLowerCase() !== 'paid')
      .reduce((sum, i) => sum + Number(i.total || 0), 0);
    const wip = totalTime - totalInvoiced - totalWriteoffs;

    return {
      hours: totalHours,
      time: totalTime,
      invoiced: totalInvoiced,
      writeoffs: totalWriteoffs,
      wip: wip,
      ar: totalAR
    };
  }, [rowsTime, rowsInv, rowsWO]);

  // handle write-off form submit
  async function submitWriteOff(e) {
    e.preventDefault();
    if (!clientFilter) {
      setErr("Please select a client first");
      return;
    }
    try {
      const writeOffData = { 
        client: Number(clientFilter), 
        date: woForm.date,
        amount: Number(woForm.amount),
        reason: woForm.reason,
        year_end: woForm.year_end ? Number(woForm.year_end) : null
      };
      
      await apiPost("/api/write-offs/", writeOffData);
      
      setWoForm({
        date: new Date().toISOString().slice(0, 10),
        amount: "",
        reason: "",
        year_end: ""
      });
      
      // Reload write-offs with current filters
      const woQs = new URLSearchParams();
      if (clientFilter) woQs.append("client", clientFilter);
      if (startDate) woQs.append("start_date", startDate);
      if (endDate) woQs.append("end_date", endDate);
      
      const wos = await apiGet(`/api/write-offs/?${woQs.toString()}`);
      let woData = listify(wos);
      
      // Apply year_end filter if set
      if (yearEnd) {
        woData = woData.filter(item => String(item.year_end) === String(yearEnd));
      }
      
      setRowsWO(woData);
      setErr("");
    } catch (e) {
      setErr(e.message);
    }
  }

  const getFilterQueryParams = () => {
    const params = [];
    if (clientFilter) params.push(`client=${clientFilter}`);
    if (startDate) params.push(`start=${startDate}`);
    if (endDate) params.push(`end=${endDate}`);
    if (yearEnd) params.push(`yearEnd=${yearEnd}`);
    if (employeeFilter) params.push(`user=${employeeFilter}`);
    return params.length > 0 ? `?${params.join("&")}` : "";
  };

  return (
    <div style={{ padding: 24, background: "#f9fafb", minHeight: "100vh" }}>
      <h2 style={{ marginTop: 0, marginBottom: 24 }}>Data Explorer</h2>
      {err && (
        <div
          style={{
            background: "#fee2e2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            padding: 10,
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          {err}
        </div>
      )}

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "flex-start",
          marginBottom: 24,
          background: "white",
          padding: 16,
          borderRadius: 12,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ position: "relative", flex: 1 }}>
          <label
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 4,
              color: "#6b7280",
            }}
          >
            Client
          </label>
          <input
            type="text"
            placeholder="Type client name…"
            value={clientQuery}
            onChange={(e) => {
              setClientQuery(e.target.value);
              setClientFilter("");
              setShowSuggestions(true);
            }}
            style={field}
          />
          {showSuggestions && clientQuery && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                background: "white",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                marginTop: 4,
                maxHeight: 200,
                overflowY: "auto",
                zIndex: 999,
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              }}
            >
              {clients
                .filter((c) =>
                  c.name.toLowerCase().includes(clientQuery.toLowerCase())
                )
                .map((c) => (
                  <div
                    key={c.id}
                    onClick={() => {
                      setClientFilter(String(c.id));
                      setClientQuery(c.name);
                      setShowSuggestions(false);
                    }}
                    style={{
                      padding: "10px 12px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f9fafb")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "white")
                    }
                  >
                    {c.name}
                  </div>
                ))}
            </div>
          )}
        </div>

        <div style={{ flex: 1 }}>
          <label
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 4,
              color: "#6b7280",
            }}
          >
            Employee (optional)
          </label>
          <select
            value={employeeFilter}
            onChange={(e) => setEmployeeFilter(e.target.value)}
            style={field}
          >
            <option value="">All employees</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.full_name || emp.username}
              </option>
            ))}
          </select>
        </div>

        <div style={{ flex: 0.8 }}>
          <label
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 4,
              color: "#6b7280",
            }}
          >
            Start Date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={field}
          />
        </div>

        <div style={{ flex: 0.8 }}>
          <label
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 4,
              color: "#6b7280",
            }}
          >
            End Date
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={field}
          />
        </div>

        <div style={{ flex: 0.6 }}>
          <label
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 4,
              color: "#6b7280",
            }}
          >
            Year End
          </label>
          <input
            type="number"
            placeholder="e.g. 2024"
            value={yearEnd}
            onChange={(e) => setYearEnd(e.target.value)}
            style={field}
          />
        </div>
      </div>

      {/* Summary Tiles */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div style={tile}>
          <div style={tileLabel}>Hours</div>
          <div style={tileValue}>{summary.hours.toFixed(2)}</div>
        </div>
        <div style={tile}>
          <div style={tileLabel}>Time Value</div>
          <div style={{ ...tileValue, color: "#3b82f6" }}>
            {money(summary.time)}
          </div>
        </div>
        <div style={tile}>
          <div style={tileLabel}>Invoiced</div>
          <div style={{ ...tileValue, color: "#10b981" }}>
            {money(summary.invoiced)}
          </div>
        </div>
        <div style={tile}>
          <div style={tileLabel}>Write-offs</div>
          <div style={{ ...tileValue, color: "#8b5cf6" }}>
            {money(summary.writeoffs)}
          </div>
        </div>
        <div style={tile}>
          <div style={tileLabel}>WIP</div>
          <div
            style={{
              ...tileValue,
              color: summary.wip < 0 ? "#ef4444" : "#f59e0b",
            }}
          >
            {money(summary.wip)}
          </div>
        </div>
        <div style={tile}>
          <div style={tileLabel}>A/R</div>
          <div style={{ ...tileValue, color: "#10b981" }}>
            {money(summary.ar)}
          </div>
        </div>
      </div>

      {/* Time Entries */}
      <div style={card}>
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>Time Entries</h3>
        {rowsTime.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 14,
              }}
            >
              <thead>
                <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                  <th
                    style={{
                      padding: "12px 8px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Date
                  </th>
                  <th
                    style={{
                      padding: "12px 8px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Employee
                  </th>
                  <th
                    style={{
                      padding: "12px 8px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Code
                  </th>
                  <th
                    style={{
                      padding: "12px 8px",
                      textAlign: "right",
                      fontWeight: 600,
                    }}
                  >
                    Hours
                  </th>
                  <th
                    style={{
                      padding: "12px 8px",
                      textAlign: "right",
                      fontWeight: 600,
                    }}
                  >
                    Amount
                  </th>
                  <th
                    style={{
                      padding: "12px 8px",
                      textAlign: "center",
                      fontWeight: 600,
                    }}
                  >
                    Year End
                  </th>
                </tr>
              </thead>
              <tbody>
                {rowsTime.map((t) => (
                  <tr key={t.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "12px 8px" }}>{t.date}</td>
                    <td style={{ padding: "12px 8px" }}>{who(t)}</td>
                    <td style={{ padding: "12px 8px" }}>{t.time_code}</td>
                    <td style={{ padding: "12px 8px", textAlign: "right" }}>
                      <a
                        href={`/time${getFilterQueryParams()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#3b82f6",
                          textDecoration: "none",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.textDecoration = "underline")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.textDecoration = "none")
                        }
                      >
                        {Number(t.hours || 0).toFixed(2)}
                      </a>
                    </td>
                    <td
                      style={{
                        padding: "12px 8px",
                        textAlign: "right",
                        fontWeight: 600,
                      }}
                    >
                      <a
                        href={`/time${getFilterQueryParams()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#3b82f6",
                          textDecoration: "none",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.textDecoration = "underline")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.textDecoration = "none")
                        }
                      >
                        {money(t.amount)}
                      </a>
                    </td>
                    <td style={{ padding: "12px 8px", textAlign: "center" }}>
                      {t.year_end || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
            {clientFilter
              ? "No time entries found for selected filters."
              : "Select a client to view time entries."}
          </div>
        )}
      </div>

      {/* Invoices */}
      <div style={card}>
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>Invoices</h3>
        {rowsInv.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 14,
              }}
            >
              <thead>
                <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                  <th
                    style={{
                      padding: "12px 8px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Date
                  </th>
                  <th
                    style={{
                      padding: "12px 8px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Number
                  </th>
                  <th
                    style={{
                      padding: "12px 8px",
                      textAlign: "right",
                      fontWeight: 600,
                    }}
                  >
                    Total
                  </th>
                  <th
                    style={{
                      padding: "12px 8px",
                      textAlign: "center",
                      fontWeight: 600,
                    }}
                  >
                    Year End
                  </th>
                  <th
                    style={{
                      padding: "12px 8px",
                      textAlign: "center",
                      fontWeight: 600,
                    }}
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {rowsInv.map((inv) => (
                  <tr
                    key={inv.id}
                    style={{ borderBottom: "1px solid #f3f4f6" }}
                  >
                    <td style={{ padding: "12px 8px" }}>{inv.date}</td>
                    <td style={{ padding: "12px 8px" }}>{inv.number}</td>
                    <td
                      style={{
                        padding: "12px 8px",
                        textAlign: "right",
                        fontWeight: 600,
                      }}
                    >
                      <a
                        href={`/invoices${getFilterQueryParams()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#10b981",
                          textDecoration: "none",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.textDecoration = "underline")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.textDecoration = "none")
                        }
                      >
                        {money(inv.total)}
                      </a>
                    </td>

                    <td style={{ padding: "12px 8px", textAlign: "center" }}>
                      {inv.year_end || "—"}
                    </td>
                    <td style={{ padding: "12px 8px", textAlign: "center" }}>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight: 500,
                          background:
                            inv.status?.toLowerCase() === "paid"
                              ? "#d1fae5"
                              : "#fee2e2",
                          color:
                            inv.status?.toLowerCase() === "paid"
                              ? "#065f46"
                              : "#991b1b",
                        }}
                      >
                        {inv.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
            {clientFilter
              ? "No invoices found for selected filters."
              : "Select a client to view invoices."}
          </div>
        )}
      </div>

      {/* Write-offs */}
      <div style={card}>
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>Write-offs</h3>
        {clientFilter && (
          <form
            onSubmit={submitWriteOff}
            style={{
              display: "flex",
              gap: 8,
              marginBottom: 16,
              padding: 12,
              background: "#f9fafb",
              borderRadius: 8,
            }}
          >
            <input
              type="date"
              value={woForm.date}
              onChange={(e) => setWoForm({ ...woForm, date: e.target.value })}
              style={{ ...field, flex: 0.8 }}
              required
            />
            <input
              type="number"
              step="0.01"
              placeholder="Amount"
              value={woForm.amount}
              onChange={(e) => setWoForm({ ...woForm, amount: e.target.value })}
              style={{ ...field, flex: 0.8 }}
              required
            />
            <input
              type="text"
              placeholder="Year End (e.g., 2024)"
              value={woForm.year_end}
              onChange={(e) =>
                setWoForm({ ...woForm, year_end: e.target.value })
              }
              style={{ ...field, flex: 0.8 }}
              required
            />
            <input
              type="text"
              placeholder="Reason"
              value={woForm.reason}
              onChange={(e) => setWoForm({ ...woForm, reason: e.target.value })}
              style={{ ...field, flex: 2 }}
              required
            />
            <button type="submit" style={{ ...btn, flex: 0.6 }}>
              Add Write-off
            </button>
          </form>
        )}
        {rowsWO.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 14,
              }}
            >
              <thead>
                <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                  <th
                    style={{
                      padding: "12px 8px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Date
                  </th>
                  <th
                    style={{
                      padding: "12px 8px",
                      textAlign: "right",
                      fontWeight: 600,
                    }}
                  >
                    Amount
                  </th>
                  <th
                    style={{
                      padding: "12px 8px",
                      textAlign: "center",
                      fontWeight: 600,
                    }}
                  >
                    Year End
                  </th>
                  <th
                    style={{
                      padding: "12px 8px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Reason
                  </th>
                </tr>
              </thead>
              <tbody>
                {rowsWO.map((w) => (
                  <tr key={w.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "12px 8px" }}>{w.date}</td>
                    <td
                      style={{
                        padding: "12px 8px",
                        textAlign: "right",
                        fontWeight: 600,
                      }}
                    >
                      {money(w.amount)}
                    </td>
                    <td style={{ padding: "12px 8px", textAlign: "center" }}>
                      {w.year_end || "—"}
                    </td>
                    <td style={{ padding: "12px 8px", color: "#6b7280" }}>
                      {w.reason || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
            {clientFilter
              ? "No write-offs found for selected filters."
              : "Select a client to view write-offs."}
          </div>
        )}
      </div>
    </div>
  );
}

const field = { 
  padding: "10px 12px", 
  borderRadius: 6, 
  border: "1px solid #e5e7eb", 
  width: "100%",
  fontSize: 14
};

const card = { 
  background: "white", 
  border: "1px solid #e5e7eb", 
  borderRadius: 12, 
  marginBottom: 24, 
  padding: 20,
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
};

const btn = { 
  padding: "10px 16px", 
  borderRadius: 6, 
  background: "#2563eb", 
  color: "white", 
  fontWeight: 600,
  border: "none",
  cursor: "pointer",
  fontSize: 14,
  transition: "background 0.15s"
};

const tile = {
  background: "white",
  borderRadius: 12,
  padding: 20,
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  border: "1px solid #e5e7eb"
};

const tileLabel = {
  fontSize: 12,
  fontWeight: 600,
  color: "#6b7280",
  marginBottom: 8,
  textTransform: "uppercase",
  letterSpacing: "0.5px"
};

const tileValue = {
  fontSize: 24,
  fontWeight: 700,
  color: "#111827"
};